
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import React, { useState } from "react";
import { useFilters } from "../context/filters-context";
import {
  FILTER_VALUE_TYPES,
  Segment,
  SegmentFilter,
  SegmentFilterType,
  SegmentOperator,
  SuccessCriterion,
  SuccessCriterionType,
} from "../types/analytics";

// ============================================================================
// FILTER TYPE OPTIONS
// ============================================================================

const FILTER_TYPE_OPTIONS: {
  value: SegmentFilterType;
  label: string;
  group: string;
}[] = [
  // Location filters
  { value: "country", label: "Country", group: "Location" },
  { value: "region", label: "Region", group: "Location" },
  { value: "city", label: "City", group: "Location" },
  { value: "timezone", label: "Timezone", group: "Location" },
  // Device & Technical
  { value: "device", label: "Device Type", group: "Device" },
  { value: "screen_size", label: "Screen Size", group: "Device" },
  { value: "screen_width", label: "Screen Width", group: "Device" },
  { value: "screen_height", label: "Screen Height", group: "Device" },
  { value: "user_agent", label: "User Agent", group: "Device" },
  // Acquisition
  { value: "referrer", label: "Referrer", group: "Acquisition" },
  { value: "source", label: "Source", group: "Acquisition" },
  { value: "landing_page", label: "Landing Page", group: "Acquisition" },
  // Behavior
  { value: "persona", label: "Persona", group: "Behavior" },
  {
    value: "session_duration",
    label: "Session Duration (sec)",
    group: "Behavior",
  },
  { value: "pages_viewed", label: "Pages Viewed", group: "Behavior" },
  // Conversion
  { value: "signed_up", label: "Signed Up", group: "Conversion" },
  { value: "contacted", label: "Contacted", group: "Conversion" },
  { value: "email", label: "Email", group: "Conversion" },
  // Page Session filters
  { value: "page_path", label: "Page Path (any)", group: "Pages" },
  { value: "experiment", label: "Experiment", group: "Pages" },
  { value: "variant", label: "Variant", group: "Pages" },
  // Timestamp filters
  { value: "session_start", label: "Session Start", group: "Time" },
  { value: "session_end", label: "Session End", group: "Time" },
];

const STRING_OPERATORS: { value: SegmentOperator; label: string }[] = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "does not contain" },
  { value: "starts_with", label: "starts with" },
  { value: "ends_with", label: "ends with" },
];

const NUMERIC_OPERATORS: { value: SegmentOperator; label: string }[] = [
  { value: "equals", label: "equals" },
  { value: "not_equals", label: "does not equal" },
  { value: "greater_than", label: "greater than" },
  { value: "greater_than_or_equals", label: "greater than or equals" },
  { value: "less_than", label: "less than" },
  { value: "less_than_or_equals", label: "less than or equals" },
  { value: "between", label: "between" },
];

const BOOLEAN_OPERATORS: { value: SegmentOperator; label: string }[] = [
  { value: "equals", label: "is" },
  { value: "not_equals", label: "is not" },
];

const DATE_OPERATORS: { value: SegmentOperator; label: string }[] = [
  { value: "equals", label: "on" },
  { value: "not_equals", label: "not on" },
  { value: "before", label: "before" },
  { value: "after", label: "after" },
  { value: "between", label: "between" },
  { value: "in_last_n_days", label: "in last N days" },
  { value: "in_last_n_hours", label: "in last N hours" },
];

function getOperatorsForType(
  filterType: SegmentFilterType
): { value: SegmentOperator; label: string }[] {
  const valueType = FILTER_VALUE_TYPES[filterType];
  switch (valueType) {
    case "number":
      return NUMERIC_OPERATORS;
    case "boolean":
      return BOOLEAN_OPERATORS;
    case "date":
      return DATE_OPERATORS;
    default:
      return STRING_OPERATORS;
  }
}

function getDefaultOperator(filterType: SegmentFilterType): SegmentOperator {
  const valueType = FILTER_VALUE_TYPES[filterType];
  switch (valueType) {
    case "number":
      return "equals";
    case "boolean":
      return "equals";
    case "date":
      return "after";
    default:
      return "contains";
  }
}

// ============================================================================
// SEGMENT FILTER ROW
// ============================================================================

interface SegmentFilterRowProps {
  filter: SegmentFilter;
  onChange: (filter: SegmentFilter) => void;
  onDelete: () => void;
}

const SegmentFilterRow: React.FC<SegmentFilterRowProps> = ({
  filter,
  onChange,
  onDelete,
}) => {
  const valueType = FILTER_VALUE_TYPES[filter.type];
  const operators = getOperatorsForType(filter.type);
  const isBetween = filter.operator === "between";
  const isLastN =
    filter.operator === "in_last_n_days" ||
    filter.operator === "in_last_n_hours";

  const handleTypeChange = (newType: SegmentFilterType) => {
    const newValueType = FILTER_VALUE_TYPES[newType];
    const currentValueType = FILTER_VALUE_TYPES[filter.type];

    if (newValueType !== currentValueType) {
      onChange({
        ...filter,
        type: newType,
        operator: getDefaultOperator(newType),
        value: newValueType === "boolean" ? "true" : "",
      });
    } else {
      onChange({ ...filter, type: newType });
    }
  };

  const renderValueInput = () => {
    if (valueType === "boolean") {
      return (
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Value</InputLabel>
          <Select
            value={String(filter.value)}
            label="Value"
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    if (valueType === "date") {
      if (isLastN) {
        return (
          <TextField
            size="small"
            label={filter.operator === "in_last_n_days" ? "Days" : "Hours"}
            type="number"
            value={filter.value}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            sx={{ width: 100 }}
            inputProps={{ min: 1 }}
          />
        );
      }

      if (isBetween) {
        const values = Array.isArray(filter.value)
          ? filter.value
          : [filter.value, ""];
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <DatePicker
              label="From"
              value={values[0] ? dayjs(values[0]) : null}
              onChange={(date) =>
                onChange({
                  ...filter,
                  value: [date?.toISOString() || "", values[1] || ""],
                })
              }
              slotProps={{ textField: { size: "small", sx: { width: 145 } } }}
            />
            <Typography variant="body2">and</Typography>
            <DatePicker
              label="To"
              value={values[1] ? dayjs(values[1]) : null}
              onChange={(date) =>
                onChange({
                  ...filter,
                  value: [values[0] || "", date?.toISOString() || ""],
                })
              }
              slotProps={{ textField: { size: "small", sx: { width: 145 } } }}
            />
          </Stack>
        );
      }

      return (
        <DatePicker
          label="Date"
          value={filter.value ? dayjs(filter.value as string) : null}
          onChange={(date) =>
            onChange({ ...filter, value: date?.toISOString() || "" })
          }
          slotProps={{ textField: { size: "small", sx: { width: 145 } } }}
        />
      );
    }

    if (valueType === "number") {
      if (isBetween) {
        const values = Array.isArray(filter.value)
          ? filter.value
          : [filter.value, ""];
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Min"
              type="number"
              value={values[0]}
              onChange={(e) =>
                onChange({
                  ...filter,
                  value: [e.target.value, values[1] || ""],
                })
              }
              sx={{ width: 100 }}
            />
            <Typography variant="body2">and</Typography>
            <TextField
              size="small"
              label="Max"
              type="number"
              value={values[1]}
              onChange={(e) =>
                onChange({
                  ...filter,
                  value: [values[0] || "", e.target.value],
                })
              }
              sx={{ width: 100 }}
            />
          </Stack>
        );
      }

      return (
        <TextField
          size="small"
          label="Value"
          type="number"
          value={filter.value}
          onChange={(e) => onChange({ ...filter, value: e.target.value })}
          sx={{ width: 120 }}
        />
      );
    }

    return (
      <TextField
        size="small"
        label="Value"
        value={
          Array.isArray(filter.value) ? filter.value.join(", ") : filter.value
        }
        onChange={(e) => onChange({ ...filter, value: e.target.value })}
        sx={{ flex: 1, minWidth: 150 }}
      />
    );
  };

  const groupedOptions = FILTER_TYPE_OPTIONS.reduce((acc, opt) => {
    if (!acc[opt.group]) acc[opt.group] = [];
    acc[opt.group].push(opt);
    return acc;
  }, {} as Record<string, typeof FILTER_TYPE_OPTIONS>);

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1 }}
      flexWrap="wrap"
    >
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Field</InputLabel>
        <Select
          value={filter.type}
          label="Field"
          onChange={(e) =>
            handleTypeChange(e.target.value as SegmentFilterType)
          }
        >
          {Object.entries(groupedOptions).map(([group, options]) => [
            <MenuItem
              key={`group-${group}`}
              disabled
              sx={{ fontWeight: 600, fontSize: 12, color: "text.secondary" }}
            >
              {group}
            </MenuItem>,
            ...options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value} sx={{ pl: 3 }}>
                {opt.label}
              </MenuItem>
            )),
          ])}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel>Operator</InputLabel>
        <Select
          value={filter.operator}
          label="Operator"
          onChange={(e) =>
            onChange({ ...filter, operator: e.target.value as SegmentOperator })
          }
        >
          {operators.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {renderValueInput()}

      <IconButton size="small" onClick={onDelete} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};

// ============================================================================
// SEGMENT BUILDER DIALOG
// ============================================================================

interface SegmentBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  initialSegment?: Segment;
  onSave: (segment: Segment) => void;
}

export const SegmentBuilderDialog: React.FC<SegmentBuilderDialogProps> = ({
  open,
  onClose,
  initialSegment,
  onSave,
}) => {
  const [name, setName] = useState(initialSegment?.name || "");
  const [description, setDescription] = useState(
    initialSegment?.description || ""
  );
  const [filters, setFilters] = useState<SegmentFilter[]>(
    initialSegment?.filters || []
  );

  // Reset state when dialog opens with different segment
  React.useEffect(() => {
    setName(initialSegment?.name || "");
    setDescription(initialSegment?.description || "");
    setFilters(initialSegment?.filters || []);
  }, [initialSegment, open]);

  const handleAddFilter = () => {
    setFilters([
      ...filters,
      {
        id: `filter-${Date.now()}`,
        type: "referrer",
        operator: "contains",
        value: "",
      },
    ]);
  };

  const handleFilterChange = (index: number, filter: SegmentFilter) => {
    setFilters(filters.map((f, i) => (i === index ? filter : f)));
  };

  const handleFilterDelete = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!name.trim()) return;

    const segment: Segment = {
      id: initialSegment?.id || `segment-${Date.now()}`,
      name: name.trim(),
      description: description.trim() || undefined,
      filters,
      createdAt: initialSegment?.createdAt || new Date().toISOString(),
    };
    onSave(segment);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {initialSegment ? "Edit Segment" : "Create Segment"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Segment Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />

          <Divider />

          <Typography variant="subtitle2">Filters</Typography>
          {filters.length === 0 ? (
            <Alert severity="info">
              No filters added. This segment will include all traffic.
            </Alert>
          ) : (
            filters.map((filter, index) => (
              <SegmentFilterRow
                key={filter.id}
                filter={filter}
                onChange={(f) => handleFilterChange(index, f)}
                onDelete={() => handleFilterDelete(index)}
              />
            ))
          )}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddFilter}
            variant="outlined"
            size="small"
            sx={{ alignSelf: "flex-start" }}
          >
            Add Filter
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!name.trim()}
        >
          Save Segment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ============================================================================
// SINGLE SEGMENT SELECTOR
// ============================================================================

interface SingleSegmentSelectorProps {
  selectedSegment: Segment;
  onSegmentChange: (segment: Segment) => void;
  label?: string;
}

export const SingleSegmentSelector: React.FC<SingleSegmentSelectorProps> = ({
  selectedSegment,
  onSegmentChange,
  label = "Segment",
}) => {
  const { savedSegments, saveSegment, deleteSegment } = useFilters();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | undefined>();

  const handleSegmentClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleSegmentSelect = (segment: Segment) => {
    onSegmentChange(segment);
    setMenuAnchor(null);
  };

  const handleCreateSegment = () => {
    setEditingSegment(undefined);
    setDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleEditSegment = (segment: Segment) => {
    setEditingSegment(segment);
    setDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleSaveSegment = (segment: Segment) => {
    saveSegment(segment);
    onSegmentChange(segment);
  };

  const handleDeleteSegment = (segmentId: string) => {
    deleteSegment(segmentId);
    // Reset to first segment if active was deleted
    if (selectedSegment.id === segmentId && savedSegments.length > 0) {
      const remaining = savedSegments.filter((s) => s.id !== segmentId);
      if (remaining.length > 0) {
        onSegmentChange(remaining[0]);
      }
    }
    setMenuAnchor(null);
  };

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <FilterListIcon fontSize="small" color="action" />
        <Typography variant="body2" color="text.secondary">
          {label}:
        </Typography>
        <Chip
          label={selectedSegment.name}
          onClick={handleSegmentClick}
          color="secondary"
          variant="outlined"
          size="small"
        />
      </Stack>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <MenuItem onClick={handleCreateSegment}>
          <AddIcon fontSize="small" sx={{ mr: 1 }} />
          Create New Segment
        </MenuItem>
        <Divider />
        {savedSegments.map((segment) => (
          <MenuItem
            key={segment.id}
            onClick={() => handleSegmentSelect(segment)}
            selected={segment.id === selectedSegment.id}
            sx={{ display: "flex", justifyContent: "space-between" }}
          >
            <span>{segment.name}</span>
            {!segment.isBuiltIn && (
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditSegment(segment);
                  }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteSegment(segment.id);
                  }}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            )}
          </MenuItem>
        ))}
      </Menu>

      <SegmentBuilderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialSegment={editingSegment}
        onSave={handleSaveSegment}
      />
    </>
  );
};

// ============================================================================
// MULTI-SEGMENT SELECTOR
// ============================================================================

interface MultiSegmentSelectorProps {
  selectedSegments: Segment[];
  onSegmentsChange: (segments: Segment[]) => void;
  label?: string;
  maxSelections?: number;
}

export const MultiSegmentSelector: React.FC<MultiSegmentSelectorProps> = ({
  selectedSegments,
  onSegmentsChange,
  label = "Segments",
  maxSelections,
}) => {
  const { savedSegments, saveSegment } = useFilters();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | undefined>();

  const handleToggleSegment = (segment: Segment) => {
    const isSelected = selectedSegments.some((s) => s.id === segment.id);
    if (isSelected) {
      onSegmentsChange(selectedSegments.filter((s) => s.id !== segment.id));
    } else {
      if (maxSelections && selectedSegments.length >= maxSelections) {
        // Replace the first selection
        onSegmentsChange([...selectedSegments.slice(1), segment]);
      } else {
        onSegmentsChange([...selectedSegments, segment]);
      }
    }
  };

  const handleCreateSegment = () => {
    setEditingSegment(undefined);
    setDialogOpen(true);
  };

  const handleEditSegment = (segment: Segment) => {
    setEditingSegment(segment);
    setDialogOpen(true);
  };

  const handleSaveSegment = (segment: Segment) => {
    saveSegment(segment);
    if (!maxSelections || selectedSegments.length < maxSelections) {
      onSegmentsChange([...selectedSegments, segment]);
    }
  };

  return (
    <>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <FilterListIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">
            {label}:
          </Typography>
          <Tooltip title="Create new segment">
            <IconButton size="small" onClick={handleCreateSegment}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {savedSegments.map((segment) => {
            const isSelected = selectedSegments.some(
              (s) => s.id === segment.id
            );
            return (
              <Chip
                key={segment.id}
                label={segment.name}
                onClick={() => handleToggleSegment(segment)}
                onDelete={
                  !segment.isBuiltIn
                    ? (e) => {
                        e.stopPropagation();
                        handleEditSegment(segment);
                      }
                    : undefined
                }
                deleteIcon={<EditIcon fontSize="small" />}
                color={isSelected ? "primary" : "default"}
                variant={isSelected ? "filled" : "outlined"}
                size="small"
                sx={{ mb: 0.5 }}
              />
            );
          })}
        </Stack>
      </Box>

      <SegmentBuilderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialSegment={editingSegment}
        onSave={handleSaveSegment}
      />
    </>
  );
};

// ============================================================================
// SUCCESS CRITERIA SELECTOR
// ============================================================================

interface SuccessCriteriaSelectorProps {
  selectedCriteria: SuccessCriterion[];
  onCriteriaChange: (criteria: SuccessCriterion[]) => void;
  label?: string;
}

export const SuccessCriteriaSelector: React.FC<
  SuccessCriteriaSelectorProps
> = ({ selectedCriteria, onCriteriaChange, label = "Success Criteria" }) => {
  const { savedSuccessCriteria, saveSuccessCriterion, deleteSuccessCriterion } =
    useFilters();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCriterion, setEditingCriterion] = useState<
    SuccessCriterion | undefined
  >();

  const handleToggleCriterion = (criterion: SuccessCriterion) => {
    const isSelected = selectedCriteria.some((c) => c.id === criterion.id);
    if (isSelected) {
      onCriteriaChange(selectedCriteria.filter((c) => c.id !== criterion.id));
    } else {
      onCriteriaChange([...selectedCriteria, criterion]);
    }
  };

  const handleCreateCriterion = () => {
    setEditingCriterion(undefined);
    setDialogOpen(true);
  };

  const handleEditCriterion = (criterion: SuccessCriterion) => {
    setEditingCriterion(criterion);
    setDialogOpen(true);
  };

  const handleSaveCriterion = (criterion: SuccessCriterion) => {
    saveSuccessCriterion(criterion);
    if (!selectedCriteria.some((c) => c.id === criterion.id)) {
      onCriteriaChange([...selectedCriteria, criterion]);
    } else {
      // Update in selection
      onCriteriaChange(
        selectedCriteria.map((c) => (c.id === criterion.id ? criterion : c))
      );
    }
  };

  const handleDeleteCriterion = (id: string) => {
    deleteSuccessCriterion(id);
    onCriteriaChange(selectedCriteria.filter((c) => c.id !== id));
  };

  return (
    <>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {label}:
          </Typography>
          <Tooltip title="Create new criterion">
            <IconButton size="small" onClick={handleCreateCriterion}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {savedSuccessCriteria.map((criterion) => {
            const isSelected = selectedCriteria.some(
              (c) => c.id === criterion.id
            );
            return (
              <Chip
                key={criterion.id}
                label={criterion.name}
                onClick={() => handleToggleCriterion(criterion)}
                onDelete={
                  !criterion.isBuiltIn
                    ? (e) => {
                        e.stopPropagation();
                        handleEditCriterion(criterion);
                      }
                    : undefined
                }
                deleteIcon={<EditIcon fontSize="small" />}
                color={isSelected ? "success" : "default"}
                variant={isSelected ? "filled" : "outlined"}
                size="small"
                sx={{ mb: 0.5 }}
              />
            );
          })}
        </Stack>
      </Box>

      <SuccessCriterionBuilderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initialCriterion={editingCriterion}
        onSave={handleSaveCriterion}
        onDelete={
          editingCriterion && !editingCriterion.isBuiltIn
            ? () => handleDeleteCriterion(editingCriterion.id)
            : undefined
        }
      />
    </>
  );
};

// ============================================================================
// SUCCESS CRITERION BUILDER DIALOG
// ============================================================================

const CRITERION_TYPE_OPTIONS: { value: SuccessCriterionType; label: string }[] =
  [
    { value: "signed_up", label: "Signed Up" },
    { value: "contacted", label: "Contacted" },
    { value: "visited_page", label: "Visited Page" },
  ];

interface SuccessCriterionBuilderDialogProps {
  open: boolean;
  onClose: () => void;
  initialCriterion?: SuccessCriterion;
  onSave: (criterion: SuccessCriterion) => void;
  onDelete?: () => void;
}

const SuccessCriterionBuilderDialog: React.FC<
  SuccessCriterionBuilderDialogProps
> = ({ open, onClose, initialCriterion, onSave, onDelete }) => {
  const [name, setName] = useState(initialCriterion?.name || "");
  const [description, setDescription] = useState(
    initialCriterion?.description || ""
  );
  const [type, setType] = useState<SuccessCriterionType>(
    initialCriterion?.type || "visited_page"
  );
  const [pagePatterns, setPagePatterns] = useState<string>(
    initialCriterion?.pagePatterns?.join(", ") || ""
  );

  // Reset state when dialog opens with different criterion
  React.useEffect(() => {
    setName(initialCriterion?.name || "");
    setDescription(initialCriterion?.description || "");
    setType(initialCriterion?.type || "visited_page");
    setPagePatterns(initialCriterion?.pagePatterns?.join(", ") || "");
  }, [initialCriterion, open]);

  const handleSave = () => {
    if (!name.trim()) return;

    const criterion: SuccessCriterion = {
      id: initialCriterion?.id || `criterion-${Date.now()}`,
      name: name.trim(),
      description: description.trim() || undefined,
      type,
      pagePatterns:
        type === "visited_page"
          ? pagePatterns
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean)
          : undefined,
      createdAt: initialCriterion?.createdAt || new Date().toISOString(),
    };
    onSave(criterion);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {initialCriterion
          ? "Edit Success Criterion"
          : "Create Success Criterion"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
          />
          <TextField
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={type}
              label="Type"
              onChange={(e) => setType(e.target.value as SuccessCriterionType)}
            >
              {CRITERION_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {type === "visited_page" && (
            <TextField
              label="Page Patterns (comma separated)"
              value={pagePatterns}
              onChange={(e) => setPagePatterns(e.target.value)}
              fullWidth
              placeholder="/pricing, /signup, /demo"
              helperText="Enter page path patterns to match (e.g., /signin, /login)"
            />
          )}
          {type === "signed_up" && (
            <Alert severity="info">
              This criterion will match sessions where the user signed up.
            </Alert>
          )}
          {type === "contacted" && (
            <Alert severity="info">
              This criterion will match sessions where the user contacted via
              form.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {onDelete && (
          <Button color="error" onClick={onDelete} sx={{ mr: "auto" }}>
            Delete
          </Button>
        )}
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!name.trim()}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Deprecated: use SuccessCriteriaSelector.

interface SuccessSegmentsSelectorProps {
  successSegmentIds: string[];
  onSuccessSegmentsChange: (segmentIds: string[]) => void;
}

export const SuccessSegmentsSelector: React.FC<
  SuccessSegmentsSelectorProps
> = ({ successSegmentIds, onSuccessSegmentsChange }) => {
  const { savedSegments, saveSegment } = useFilters();
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleToggle = (segmentId: string) => {
    if (successSegmentIds.includes(segmentId)) {
      onSuccessSegmentsChange(
        successSegmentIds.filter((id) => id !== segmentId)
      );
    } else {
      onSuccessSegmentsChange([...successSegmentIds, segmentId]);
    }
  };

  const handleCreateSegment = () => {
    setDialogOpen(true);
  };

  const handleSaveSegment = (segment: Segment) => {
    saveSegment(segment);
    // Auto-select newly created segment as success segment
    onSuccessSegmentsChange([...successSegmentIds, segment.id]);
  };

  return (
    <>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Success Segments</Typography>
          <Typography variant="caption" color="text.secondary">
            (select segments representing successful conversions)
          </Typography>
          <Tooltip title="Create new segment">
            <IconButton size="small" onClick={handleCreateSegment}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {savedSegments.map((segment) => {
            const isSuccess = successSegmentIds.includes(segment.id);
            return (
              <Chip
                key={segment.id}
                label={segment.name}
                onClick={() => handleToggle(segment.id)}
                color={isSuccess ? "success" : "default"}
                variant={isSuccess ? "filled" : "outlined"}
                size="small"
                sx={{ mb: 0.5 }}
              />
            );
          })}
        </Stack>
      </Box>

      <SegmentBuilderDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveSegment}
      />
    </>
  );
};
